import {getFormMessages} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {TFunction, useTranslation} from '@wix/yoshi-flow-editor'
import React, {useMemo, useRef} from 'react'
import {useCurrencyFormatter} from '../../../../../../commons/hooks/currency'
import {useAppActions, useAppSelector} from '../../../hooks/runtime'
import {
  getExpandedTicketIndex,
  getIndividualReservationTickets,
  getTicketDetailsOld,
  isAllTicketDetailsValid,
} from '../../../selectors/checkout'
import {getEvent} from '../../../selectors/event'
import {getSelectedTicketsArray} from '../../../selectors/selected-tickets'
import {REGISTRATION_FORM_ID} from '../../form/constants'
import {FormSubmitButton} from '../../form-submit-button'
import {TicketsDetailsProps} from './interfaces'
import {TicketDetails} from './ticket-details'
import {TicketsDetailsContext} from './tickets-details-context'
import s from './tickets-details.scss'

export const TicketsDetails = ({editing, onSubmit}: TicketsDetailsProps) => {
  const {t} = useTranslation()
  const {getFormattedMoney} = useCurrencyFormatter()
  const {nextFormClicked, setExpandedTicketIndex} = useAppActions()
  const submitExpandedFormRef = useRef<(() => void) | null>(null)

  const selectedTickets = useAppSelector(getSelectedTicketsArray)
  const individualTickets = useAppSelector(getIndividualReservationTickets)
  const details = useAppSelector(state => individualTickets.map((_, index) => getTicketDetailsOld(state, index)))
  const allTicketDetailsValid = useAppSelector(isAllTicketDetailsValid)
  const messages = useAppSelector(state => getFormMessages(getEvent(state)))
  const expandedTicketIndex = useAppSelector(getExpandedTicketIndex)

  const ticketFormSubmitHandlers = useMemo(
    () => ({
      setExpandedFormSubmit: (submitFn: () => void) => {
        submitExpandedFormRef.current = submitFn
      },
      resetExpandedFormSubmit: () => {
        submitExpandedFormRef.current = () => {}
      },
    }),
    [],
  )

  const toggleExpandedTicket = (index: number) => {
    setExpandedTicketIndex(expandedTicketIndex === index ? -1 : index)
  }

  const expandNextTicket = () => {
    nextFormClicked()
  }

  const collapseTickets = () => {
    setExpandedTicketIndex(-1)
  }

  const submit = () => {
    onSubmit(details)

    if (allTicketDetailsValid) {
      collapseTickets()
    } else {
      setTimeout(() => submitExpandedFormRef.current?.(), 10)
    }
  }

  return (
    <TicketsDetailsContext.Provider value={ticketFormSubmitHandlers}>
      <div className={s.container}>
        {individualTickets.map(({ticket, ticketDetails}, index) => (
          <TicketDetails
            key={index}
            formId={`${REGISTRATION_FORM_ID}${index}`}
            index={index}
            ticketName={ticket.name}
            subtitle={getSubtitle(ticket, t, getFormattedMoney, ticketDetails)}
            editable={editing}
            expanded={index === expandedTicketIndex}
            hideButton={index === selectedTickets.length - 1}
            onExpand={() => toggleExpandedTicket(index)}
            onSubmit={expandNextTicket}
          />
        ))}
        {editing && (
          <div className={s.button}>
            <FormSubmitButton
              dataHook={DH.FORM_BUTTON}
              text={messages.checkout.submitActionLabel || messages.rsvp.submitActionLabel}
              onClick={submit}
            />
          </div>
        )}
      </div>
    </TicketsDetailsContext.Provider>
  )
}

const getSubtitle = (
  ticket: wix.events.ticketing.TicketDefinition,
  t: TFunction,
  getFormattedMoney: (price: wix.events.Money) => string,
  ticketDetails?: wix.events.ticketing.TicketDetails,
) => {
  const parts = []

  if (ticketDetails?.pricingOptionName) {
    parts.push(
      `${ticketDetails.pricingOptionName}: ${getFormattedMoney({
        amount: ticketDetails.priceOverride,
        currency: ticket.price.currency,
      })}`,
    )
  }

  if (ticketDetails?.seatId) {
    const area = !!ticketDetails.areaLabel
    const table = !!ticketDetails.tableLabel

    const section = ticketDetails.sectionLabel ? `${t('seatings_sector')}: ${ticketDetails.sectionLabel}` : ''
    const elementType = area
      ? `${t('seatings_area')}: ${ticketDetails.areaLabel}`
      : table
      ? `${t('seatings_table')}: ${ticketDetails.tableLabel}`
      : `${t('seatings_row')}: ${ticketDetails.rowLabel}`
    const seat = !area ? `${t('seatings_seat')}: ${ticketDetails.seatLabel}` : ''

    parts.push([section, elementType, seat].filter(val => val).join(', '))
  }

  return parts.join(', ')
}
